// NotificationBell.js
import React, { useState, useEffect, useRef } from "react";
import "./NotificationBell.css";
import { useNavigate } from "react-router-dom";
import { fetchNotifications } from "../Apis/AdminApis";

const NotificationBell = ({ notificationsData, setNotificationsData, role = "admin" }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  // Determine notification page path based on role
  const notificationsPath = role === "superadmin" ? "/superadmin/notifications" : "/admin/notifications";
  
  // Storage key based on role
  const storageKey = role === "superadmin" ? "superadmin_persistent_unread" : "persistent_unread";
  
  const [persistentUnread, setPersistentUnread] = useState(
    Number(localStorage.getItem(storageKey)) || 0
  );

  useEffect(() => {
    if (!isOpen) return;

    const loadList = async () => {
      setIsLoading(true);
      try {
        const response = await fetchNotifications("list");
        if (response?.error) {
          console.error("Notifications error:", response.error);
          return;
        }
        setNotificationsData((prev) => ({
          unread_count: 0,
          notifications: Array.isArray(response?.notifications)
            ? response.notifications
            : [],
        }));
        setPersistentUnread(0);
        localStorage.setItem(storageKey, 0);
      } catch (error) {
        console.error("❌ Failed to load notifications list:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadList();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!notificationsData) return;
    const list = notificationsData.notifications || [];
    const unread = notificationsData.unread_count || 0;

    setNotifications(list);
    setPersistentUnread(unread);
    localStorage.setItem(storageKey, unread);
  }, [notificationsData]);

  const parseBackendDate = (dateString) => {
    if (!dateString) return null;
    const iso = new Date(dateString);
    if (!isNaN(iso)) return iso;
    const match = dateString.match(/(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) {
      let [, day, month, year, hour, minute, meridiem] = match;
      hour = parseInt(hour, 10);
      if (meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;
      return new Date(year, month - 1, day, hour, minute);
    }
    return null;
  };

  const formatTime = (dateString) => {
    const date = parseBackendDate(dateString);
    if (!date) return dateString || "N/A";

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return dateString;
  };

  // Get notification icon based on type
  const getNotificationIcon = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_login') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF4500" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M8 8h8M8 12h6" />
          <circle cx="18" cy="8" r="2" fill="#FF4500" stroke="none" />
          <path d="M20 6l2-2M22 6l-2-2" stroke="#FF4500" />
        </svg>
      );
    }

    if (type === 'device_approved') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00B894" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M8 8h8M8 12h6" />
          <circle cx="18" cy="8" r="2" fill="#00B894" stroke="none" />
          <path d="M17 7l2 2 4-4" stroke="#00B894" strokeWidth="2.5" />
        </svg>
      );
    }

    if (type === 'device_rejected') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF4757" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M8 8h8M8 12h6" />
          <circle cx="18" cy="8" r="2" fill="#FF4757" stroke="none" />
          <path d="M20 6l-2 2M18 6l2 2" stroke="#FF4757" strokeWidth="2.5" />
        </svg>
      );
    }

    // Company notifications (SuperAdmin)
    if (type.startsWith("device")) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2">
          <rect x="4" y="4" width="16" height="12" rx="2" />
          <path d="M2 20h20" />
        </svg>
      );
    }

    if (type === "company_pending" || type === "company_registered") {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    }

    if (type === "company_approved") {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00B894" strokeWidth="2">
          <path d="M20 6L9 17L4 12" strokeWidth="2.5" />
        </svg>
      );
    }

    if (type === "company_rejected") {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF4757" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    }

    // Default
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4450A2" strokeWidth="2">
        <rect x="2" y="4" width="20" height="16" rx="2" />
      </svg>
    );
  };

  // Get notification title based on type
const getNotificationTitle = (notification) => {
  const title = notification.title || "New Notification";
  const type = notification.notification_type || "";

  let color = "#4450A2";

  // Green - Approved
  if (type.includes("APPROVED") || type.includes("APPROVE")) {
    color = "#00B894";
  }

  // Red - Rejected / Revoked / Withdrawn
  else if (
    type.includes("REJECTED") ||
    type.includes("REJECT") ||
    type.includes("REVOKED") ||
    type.includes("WITHDRAWN")
  ) {
    color = "#FF4757";
  }

  // Orange - New / Registration / Pending / Request
  else if (
    type.includes("PENDING") ||
    type.includes("REQUEST") ||
    type.includes("REGISTRATION") ||
    type.includes("NEW")
  ) {
    color = "#FF9800";
  }

  // Orange - Login
  else if (type.includes("LOGIN")) {
    color = "#FF4500";
  }

  return (
    <span
      style={{
        color: color,
        fontWeight: "700",
      }}
    >
      {title}
    </span>
  );
};

  // Get status badge based on type
  const getStatusBadge = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_approved') {
      return <span className="notification-status-badge approved">✓ Approved</span>;
    }
    if (type === 'device_rejected') {
      return <span className="notification-status-badge rejected">✗ Rejected</span>;
    }
    if (type === 'device_login') {
      return <span className="notification-status-badge pending">⏳ Pending OTP</span>;
    }

    // Company notifications (SuperAdmin)
    if (type === "company_pending" || type === "company_registered") {
      return <span className="notification-status-badge pending">⏳ Pending</span>;
    }
    if (type === "company_approved") {
      return <span className="notification-status-badge approved">✓ Approved</span>;
    }
    if (type === "company_rejected") {
      return <span className="notification-status-badge rejected">✗ Rejected</span>;
    }

    return null;
  };

  // Get color based on notification type
  const getNotificationColor = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_approved') return '#00B894';
    if (type === 'device_rejected') return '#FF4757';
    if (type === 'device_login') return '#FF4500';

    // Company notifications (SuperAdmin)
    if (type.startsWith("device")) return '#6C5CE7';
    if (type === "company_pending" || type === "company_registered") return '#FF9800';
    if (type === "company_approved") return '#00B894';
    if (type === "company_rejected") return '#FF4757';

    return '#4450A2';
  };

  // Get background color based on notification type
  const getNotificationBgColor = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_approved') return 'rgba(0, 184, 148, 0.1)';
    if (type === 'device_rejected') return 'rgba(255, 71, 87, 0.1)';
    if (type === 'device_login') return 'rgba(255, 69, 0, 0.1)';

    // Company notifications (SuperAdmin)
    if (type.startsWith("device")) return 'rgba(108, 92, 231, 0.1)';
    if (type === "company_pending" || type === "company_registered") return 'rgba(255, 152, 0, 0.1)';
    if (type === "company_approved") return 'rgba(0, 184, 148, 0.1)';
    if (type === "company_rejected") return 'rgba(255, 71, 87, 0.1)';

    return 'rgba(68, 80, 162, 0.1)';
  };

  const displayedNotifications = notifications.slice(0, 2);
  const hasMoreNotifications = notifications.length > 2;

  const handleViewAll = () => {
    setIsOpen(false);
    navigate(notificationsPath);
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" />
          <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" />
        </svg>

        {persistentUnread > 0 && (
          <span className="notification-badge">{persistentUnread}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <span className="notification-count">
              {notifications.filter(n => !n.is_read).length} unread
            </span>
          </div>

          <div className="notification-list">
            {isLoading && notifications.length === 0 ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">No new notifications</div>
            ) : (
              <>
                {displayedNotifications.map((notification) => {
                  const color = getNotificationColor(notification);
                  const bgColor = getNotificationBgColor(notification);
                  
                  return (
                    <div
                      key={notification.id}
                      className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                      style={{
                        borderLeft: !notification.is_read ? `3px solid ${color}` : 'none'
                      }}
                    >
                      <div className="notification-icon" style={{
                        background: !notification.is_read ? bgColor : 'rgba(68, 80, 162, 0.1)'
                      }}>
                        {getNotificationIcon(notification)}
                      </div>

                   <div className="notification-content">
  <div className="notification-title">
    {getNotificationTitle(notification)}
  </div>

  {role === "superadmin" && notification.company_name && (
    <div
      className="notification-company"
      style={{
        fontSize: "12px",
        color: "#666",
        marginTop: "2px",
        fontWeight: "600",
      }}
    >
      {notification.company_name}
    </div>
  )}

  {notification.message && (
    <div
      className="notification-message"
      style={{
        fontSize: "12px",
        color: "#555",
        marginTop: "4px",
      }}
    >
      {notification.message}
    </div>
  )}

                        <div className="notification-details">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                            <span className="notification-time" style={{
                              background: !notification.is_read ? bgColor : 'transparent',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              color: !notification.is_read ? color : '#999'
                            }}>
                              {formatTime(notification.created_on)}
                            </span>
                            
                            {getStatusBadge(notification)}
                          </div>
                        </div>
                      </div>

                      {!notification.is_read && (
                        <span className="notification-dot" style={{
                          background: color
                        }}></span>
                      )}
                    </div>
                  );
                })}

                {hasMoreNotifications && (
                  <div className="notification-more-indicator">
                    <span className="more-count">
                      +{notifications.length - 2} more notification{notifications.length - 2 !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

        <div className="notification-footer">
  {notifications.length > 0 && (
    <button className="view-all-btn" onClick={handleViewAll}>
      View All Notifications ({notifications.length})
    </button>
  )}
</div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;